import React, { useEffect, useRef, useState } from "react";
import { MapContainer, TileLayer, Marker, Popup, GeoJSON, useMap } from "react-leaflet";
import { divIcon } from "leaflet";
import logoIcon from "./images/Big Air Logo.png";
import "./App.css";

// Function to create a custom marker icon
const createCustomIcon = (isComingSoon) => {
  return divIcon({
    html: `
      <div class="marker-container ${isComingSoon ? "coming-soon-marker" : "open-park-marker"}">
        <img src="${logoIcon}" class="marker-image ${isComingSoon ? "coming-soon-image" : "open-park-image"}" />
      </div>
    `,
    className: isComingSoon ? "custom-marker coming-soon-marker" : "custom-marker open-park-marker",
    iconSize: [30, 40],
    iconAnchor: [15, 40],
    popupAnchor: [0, -40],
  });
};

// **Expand Bounds to Allow Popups Above the US**
const US_BOUNDS = [
  [24.396308, -135.0], // Bottom-left (Southern California)
  [58.0, -66.93457], // Top-right (Maine)
];

// **Optimized Map Default Center & Zoom**
const DEFAULT_CENTER = [37.8, -96.0]; // Centered over the US
const DEFAULT_ZOOM = window.innerWidth < 768 ? 3 : 4; // 3 on mobile, 5 on larger screens


function LeafletMap({ markers, selectedStoreId }) {
  const mapRef = useRef(null);
  const [usBorders, setUsBorders] = useState(null);
  const [maskBorders, setMaskBorders] = useState(null);

  // Fetch US state borders & world mask
  useEffect(() => {
    fetch("https://raw.githubusercontent.com/PublicaMundi/MappingAPI/master/data/geojson/us-states.json")
      .then((response) => response.json())
      .then((data) => {
        // Remove Alaska, Puerto Rico, and Hawaii
        const filteredStates = {
          type: "FeatureCollection",
          features: data.features.filter(
            (feature) => !["Alaska", "Puerto Rico", "Hawaii"].includes(feature.properties.name)
          ),
        };
        setUsBorders(filteredStates);
      })
      .catch((error) => console.error("Error fetching US state borders:", error));

    // Fetch world borders to darken or remove non-US areas
    fetch("https://raw.githubusercontent.com/johan/world.geo.json/master/countries.geo.json")
      .then((response) => response.json())
      .then((data) => {
        // Exclude the US from the dark mask
        const maskData = {
          type: "FeatureCollection",
          features: data.features.filter(
            (feature) => feature.properties.ADMIN !== "United States of America"
          ),
        };
        setMaskBorders(maskData);
      })
      .catch((error) => console.error("Error fetching world mask:", error));
  }, []);

  // Force Redraw for iFrame Fix
  useEffect(() => {
    if (mapRef.current) {
      setTimeout(() => {
        mapRef.current.invalidateSize();
      }, 500);
    }
  }, []);

  // Ensure map view fits the US
  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.fitBounds(US_BOUNDS, { padding: [50, 50] });
    }
  }, []);

  return (
    <MapContainer
      center={DEFAULT_CENTER}
      zoom={DEFAULT_ZOOM}
      style={{ height: "80vh", width: "100%"
        // , borderTop: "1px solid #ff9c00 !important" 
      }}
      whenCreated={(map) => (mapRef.current = map)}
      zoomControl={true}
      maxBounds={US_BOUNDS}
      maxBoundsViscosity={1.0}
      minZoom={3}
      maxZoom={7}
    >
      {/* Use OpenStreetMap Tiles */}
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
      />

    
     {/* Make all non-US countries white */}
{maskBorders && (
  <GeoJSON
    data={maskBorders}
    style={() => ({
      color: "transparent", // Hide country outlines
      fillColor: "#ffffff", // Make non-US areas white
      fillOpacity: 1, // Fully filled
    })}
    pane="tilePane" // Ensure this is behind other layers
  />
)}

{/* US State Borders (White Outline, Orange Fill) */}
{usBorders && (
  <GeoJSON
    data={usBorders}
    style={() => ({
      color: "#ffffff", // White state borders
      weight: 2,
      opacity: 1,
      fillColor: "#ff9c00", // Orange US mainland
      fillOpacity: 1,
    })}
    pane="overlayPane" // Ensure this is on top
  />
)}


      {/* Render "Coming Soon" markers FIRST (Lower Z-Index) */}
      {markers
        .filter((marker) => marker.comingSoon)
        .map((marker) => (
          <StoreMarker key={marker.id} marker={marker} isSelected={marker.id === selectedStoreId} />
        ))}

      {/* Render Open parks LAST (Higher Z-Index) */}
      {markers
        .filter((marker) => !marker.comingSoon)
        .map((marker) => (
          <StoreMarker key={marker.id} marker={marker} isSelected={marker.id === selectedStoreId} />
        ))}
    </MapContainer>
  );
}

// StoreMarker Component
function StoreMarker({ marker, isSelected }) {
  const markerRef = useRef(null);
  const map = useMap();
  const bigAirUrl = `https://www.bigairusa.com/${marker.name.toLowerCase()}`;
  const googleMapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(`Big Air Trampoline Park, ${marker.address}`)}`;

  useEffect(() => {
    if (isSelected && markerRef.current) {
      markerRef.current.openPopup();
      map.setView([marker.latitude, marker.longitude], 15, { animate: true });
    }
  }, [isSelected, map, marker.latitude, marker.longitude]);

  return (
    <Marker position={[marker.latitude, marker.longitude]} icon={createCustomIcon(marker.comingSoon)} ref={markerRef}>
      <Popup>
        <div className={`popup-content ${marker.comingSoon ? "coming-soon-overlay" : ""}`}>
          <h3>Big Air {marker.name}</h3>
          <p>{marker.address}</p>
          {marker.comingSoon ? (
            <p>
              <strong>Coming Soon!</strong>
            </p>
          ) : (
            <div className="button-container">
              <a href={bigAirUrl} target="_blank" rel="noopener noreferrer" className="visit-button">
                Visit Website
              </a>
              <a href={googleMapsUrl} target="_blank" rel="noopener noreferrer" className="directions-button">
                Get Directions
              </a>
            </div>
          )}
        </div>
      </Popup>
    </Marker>
  );
}

export default LeafletMap;
